import * as React from 'react';

import { TempTab } from './components/temp_tab';
import { VentilationTab } from './components/ventilation_tab';
import { EnergyTab } from './components/energy_tab';
import { layoutFor, useViewportWidth } from './util';
import { HeaderStatusContext } from './util/header_status';
import { PAGE_BG } from './components/ui/tokens';

const TABS: { hash: string; label: string }[] = [
  { hash: '#temp', label: 'Temp' },
  { hash: '#vent', label: 'Vent' },
  { hash: '#energy', label: 'Energy' },
];

// Bootstrap 4 draws a focus ring on clicked / tapped buttons too; keep it for keyboard focus only.
const APP_CSS = `.num{font-variant-numeric:tabular-nums}
button:focus:not(:focus-visible){outline:none}`;

// The selected tab lives in the URL hash, so a refresh (or a bookmark) opens the same tab.
function tabFromUrl(): string {
  const hash = window.location.hash;
  if (hash === '#solar') {
    return '#energy'; // the Solar tab's power flow moved into the Energy tab
  }
  if (TABS.some((t) => t.hash === hash)) {
    return hash;
  }
  // `?mock=energy|temp|vent` previews a tab with generated data, so open that tab directly.
  const mock = new URLSearchParams(window.location.search).get('mock') ?? '';
  return mock.startsWith('energy') ? '#energy' : mock.startsWith('vent') ? '#vent' : '#temp';
}

// The favicon mark ("Dawn").
const BrandMark = (props: { size: number }) => (
  <svg width={props.size} height={props.size} viewBox="0 0 32 32" aria-hidden="true">
    <rect width="32" height="32" rx="8" fill="#17233b" />
    <path d="M9 21a7 7 0 0 1 14 0z" fill="#f2a516" />
    <path
      d="M16 11.5V8.5M22.7 14.3l2.1-2.1M9.3 14.3l-2.1-2.1"
      stroke="#f2a516"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path d="M6 21h20" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const App = () => {
  const [selectedTab, setSelectedTab] = React.useState(tabFromUrl);
  const [status, setStatus] = React.useState<React.ReactNode>(null);
  const size = layoutFor(useViewportWidth());
  const selectTab = (tab: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.history.replaceState(null, '', tab);
    setSelectedTab(tab);
  };
  React.useEffect(() => {
    const onHashChange = () => setSelectedTab(tabFromUrl());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  React.useEffect(() => {
    if (window.location.hash === '#solar') {
      window.history.replaceState(null, '', '#energy');
    }
  }, []);
  React.useEffect(() => {
    document.body.style.background = PAGE_BG;
  }, []);

  const phone = size === 'phone';
  const web = size === 'web';
  const brand = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <BrandMark size={size === 'tablet' ? 32 : 30} />
      <span style={{ fontWeight: 600, fontSize: size === 'tablet' ? 19 : 18, letterSpacing: '-0.01em' }}>
        Koidukiire
      </span>
    </div>
  );
  const nav = (
    <nav
      aria-label="Sections"
      style={
        web
          ? { display: 'flex', gap: 4 }
          : {
              display: 'flex',
              gap: 4,
              padding: 4,
              background: '#e9e8e3',
              borderRadius: 12,
              margin: phone ? '0 16px' : undefined,
            }
      }
    >
      {TABS.map((t) => {
        const on = t.hash === selectedTab;
        return (
          <a
            key={t.hash}
            href={t.hash}
            onClick={selectTab(t.hash)}
            aria-current={on ? 'page' : undefined}
            style={{
              flex: phone ? 1 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 44,
              boxSizing: 'border-box',
              padding: phone ? '0' : web ? '0 16px' : '0 18px',
              fontSize: 14,
              fontWeight: on ? 600 : 400,
              color: on ? '#0b0b0b' : '#52514e',
              textDecoration: 'none',
              borderRadius: 9,
              background: on ? (web ? '#ecebe6' : '#fcfcfb') : undefined,
              boxShadow: on && !web ? '0 1px 2px rgba(11,11,11,0.08)' : undefined,
            }}
          >
            {t.label}
          </a>
        );
      })}
    </nav>
  );

  let header: React.ReactNode;
  if (phone) {
    header = (
      <>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '16px 16px 12px',
          }}
        >
          {brand}
          {status}
        </header>
        {nav}
      </>
    );
  } else if (web) {
    header = (
      <header
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px 24px',
          padding: '7px 32px',
          background: '#fcfcfb',
          borderBottom: '1px solid #e1e0d9',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
          {brand}
          {nav}
        </div>
        {status}
      </header>
    );
  } else {
    header = (
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '20px 24px 0',
        }}
      >
        {brand}
        {nav}
        <div style={{ minWidth: 160, display: 'flex', justifyContent: 'flex-end' }}>{status}</div>
      </header>
    );
  }

  return (
    <HeaderStatusContext.Provider value={setStatus}>
      <style>{APP_CSS}</style>
      <div
        style={{
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          color: '#0b0b0b',
          lineHeight: 'normal',
          minHeight: '100vh',
        }}
      >
        {header}
        {(() => {
          switch (selectedTab) {
            case '#vent':
              return <VentilationTab />;
            case '#energy':
              return <EnergyTab />;
            default:
              return <TempTab />;
          }
        })()}
      </div>
    </HeaderStatusContext.Provider>
  );
};
